import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import SelectModal from '~/components/SelectModal'

const countryOptions = [
  { value: 'co', title: 'Colombia', subtitle: 'América del Sur', icon: 'material-symbols-light:pin-drop-outline' },
  { value: 'mx', title: 'México', subtitle: 'América del Norte', icon: 'material-symbols-light:pin-drop-outline' },
  { value: 'ar', title: 'Argentina', subtitle: 'América del Sur', icon: 'material-symbols-light:pin-drop-outline' },
  { value: 'pe', title: 'Perú', subtitle: 'América del Sur', icon: 'material-symbols-light:pin-drop-outline' },
  { value: 'cl', title: 'Chile', subtitle: 'América del Sur', icon: 'material-symbols-light:pin-drop-outline' },
  { value: 'ec', title: 'Ecuador', subtitle: 'América del Sur', icon: 'material-symbols-light:pin-drop-outline' }
]

const largeDataset = Array.from({ length: 10000 }, (_, i) => ({
  value: `user-${i + 1}`,
  title: `Usuario Delta-${i + 1}`,
  subtitle: `ID: ${String(i + 1).padStart(5, '0')} · Active Node`
}))

const SelectModalPage: Component = () => {
  const [sc, setSc] = createSignal<string | undefined>()
  const [mc, setMc] = createSignal<string[]>([])
  const [ld, setLd] = createSignal<string[]>([])

  return (
    <>
      <Title>SelectModal - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">SelectModal</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Selector inmersivo para grandes volúmenes de datos. SelectModal traslada la experiencia de selección a un diálogo dedicado, permitiendo búsquedas complejas y gestión de multiselección con
            un enfoque en la ergonomía y el rendimiento.
          </p>
        </div>

        <div class="space-y-12">
          {/* Interface de Selección */}
          <Card>
            <Card.Header
              title="Experiencia Focalizada"
              subtitle="Ideal para selecciones que requieren contexto adicional o listas extensas."
              icon={<Icon icon="material-symbols-light:movie-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="space-y-4">
                  <Badge variant="soft" color="info">
                    Single Select
                  </Badge>
                  <SelectModal label="Sede Principal" options={countryOptions} value={sc()} onChange={setSc} search placeholder="Seleccionar país..." />
                </div>
                <div class="space-y-4">
                  <Badge variant="soft" color="secondary">
                    Multiple Select
                  </Badge>
                  <SelectModal label="Zonas de Cobertura" options={countryOptions} value={mc()} onChange={setMc} multiple search placeholder="Elegir múltiples..." />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Stress Test & Performance */}
          <Card>
            <Card.Header
              title="Rendimiento Masivo"
              subtitle="Optimización mediante virtualización automática para datasets de alta densidad."
              icon={<Icon icon="material-symbols-light:bolt-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="flex items-center justify-between flex-wrap gap-4">
                  <div class="max-w-md">
                    <h4 class="font-bold text-foreground mb-2">Simulación: 10,000 Registros</h4>
                    <p class="text-sm text-foreground-subtle leading-relaxed italic">
                      SelectModal utiliza el motor de virtualización para renderizar solo los elementos visibles, permitiendo una interacción fluida en datasets de gran escala.
                    </p>
                  </div>
                  <div class="p-4 bg-slate-900 rounded-2xl shadow-lg border border-slate-700">
                    <SelectModal label="Explorador de Usuarios" options={largeDataset} value={ld()} onChange={setLd} multiple search placeholder="Buscar en 10,000 usuarios..." />
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div class="p-4 bg-success-soft rounded-xl border border-success/25">
                    <div class="text-xs font-black text-emerald-600 uppercase mb-1">DOM Count</div>
                    <div class="text-2xl font-bold font-mono text-on-success-soft">~24 Items</div>
                  </div>
                  <div class="p-4 bg-info-soft rounded-xl border border-info/25">
                    <div class="text-xs font-black text-blue-600 uppercase mb-1">Dataset Size</div>
                    <div class="text-2xl font-bold font-mono text-on-info-soft">10,000</div>
                  </div>
                  <div class="p-4 bg-warning-soft rounded-xl border border-warning/25">
                    <div class="text-xs font-black text-amber-600 uppercase mb-1">Selected</div>
                    <div class="text-2xl font-bold font-mono text-on-warning-soft">{ld().length}</div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente SelectModal."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">options</td>
                      <td class="px-6 py-4 italic text-xs">OptionType[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4">Array de objetos {`{ value, title, subtitle, icon }`}.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">search</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Muestra un campo de búsqueda en la parte superior del modal.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">multiple</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Habilita multiselección con checkboxes persistentes.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">error</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Mensaje de validación debajo del trigger.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SelectModalPage
